import { useState } from 'react'
import clsx from 'clsx'

import type { DealBookColumn, DealBookDeal } from '@/services/dealBookImport'

import { isProblemRow } from './dealBookRows'

/** What happens to a deal, in the words the dialog uses, and its tone. */
export type DealBookOutcome = { label: string; tone: string }

/** A datapoint the file does not state, in the column it leaves blank. */
function gapIn(deal: DealBookDeal, column: DealBookColumn) {
  return (deal.gaps ?? []).find((gap) => column.fields.includes(gap.field))
}

/** The warnings about a value in this column (left blank, or settled by a rule). */
function warningsIn(deal: DealBookDeal, column: DealBookColumn): string[] {
  return (deal.warning_fields ?? []).filter((entry) => column.fields.includes(entry.field)).map((entry) => entry.warning)
}

/** Everything said about a deal, most decisive first: why it is not imported, what goes to review, what is
 *  left blank, then how the file was read. */
function issuesOf(deal: DealBookDeal): { text: string; tone: string }[] {
  return [
    ...deal.reasons.map((text) => ({ text, tone: 'text-orange-200/90' })),
    ...(deal.gaps ?? []).map((gap) => ({ text: `Opens in the Data Intelligence Review: ${gap.label}, ${gap.why}`, tone: 'text-amber-200/80' })),
    ...deal.warnings.map((text) => ({ text, tone: 'text-stone-400' })),
    ...(deal.notes ?? []).map((text) => ({ text, tone: 'text-stone-600' })),
  ]
}

/** Lines an Issues cell shows before "+N more", so a long list does not make every row tall. */
const ISSUES_SHOWN = 2
/** About what one line of the Issues column holds; a longer issue is cut, so the row offers to show it all. */
const ONE_LINE = 70

const HEAD = 'sticky top-0 z-20 whitespace-nowrap border-b border-amber-300/10 bg-[#121212] px-2 py-2 text-center text-[9px] font-medium uppercase tracking-[0.1em] text-stone-500'

/**
 * Every deal in the book, one row each, under the Pipeline report's headers, with
 * each cell as the grid will print it once the deal is created (the server reads
 * it through the grid's own column resolver). The deal stays put on the left
 * while the columns scroll; what will not be imported, what the file does not
 * state, what is left blank and what a platform default fills are marked in the
 * cell they concern, and each header counts the deals with a problem in it.
 */
export default function DealBookPreviewGrid({
  columns,
  deals,
  outcome,
}: {
  columns: DealBookColumn[]
  deals: DealBookDeal[]
  outcome: (deal: DealBookDeal) => DealBookOutcome
}) {
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set())
  // The first column is the deal itself; it is drawn as the sticky row header.
  const fields = columns.filter((column) => column.id !== 'property')
  // A deal already in Helios is never changed, so nothing in its row counts against a column.
  const imported = deals.filter((deal) => deal.action !== 'exists')
  const flagged = new Map(fields.map((column) => [
    column.id,
    imported.filter((deal) => gapIn(deal, column) || warningsIn(deal, column).length).length,
  ] as const))
  function toggle(key: string) {
    setOpened((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }
  return (
    <table className="min-w-full border-separate border-spacing-0 text-[10px] tabular-nums" aria-label="Deals in the book">
      <thead>
        <tr>
          <th scope="col" className={clsx(HEAD, 'left-0 z-30 border-r border-r-amber-300/10 text-left')}>
            {columns.find((column) => column.id === 'property')?.header ?? 'Deal'}
          </th>
          <th scope="col" className={clsx(HEAD, 'text-left')}>Issues</th>
          {fields.map((column) => {
            const count = flagged.get(column.id) ?? 0
            return (
              <th key={column.id} scope="col" className={HEAD} data-column={column.id}>
                {column.header}
                {count ? (
                  <span
                    className="ml-1 rounded bg-orange-400/15 px-1 font-mono normal-case tracking-normal text-orange-200"
                    title={`${count} deal${count === 1 ? '' : 's'} with a value in this column the file does not state or that is left blank`}
                    data-testid="deal-book-column-flag"
                  >
                    {count}
                  </span>
                ) : null}
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {deals.length === 0 ? (
          <tr>
            <td colSpan={fields.length + 2} className="px-3 py-6 text-left text-[11px] text-stone-500">
              No deals match this filter.
            </td>
          </tr>
        ) : deals.map((deal, index) => {
          const key = `${deal.deal}-${index}`
          const result = outcome(deal)
          const untouched = deal.action === 'exists'
          const flag = deal.action === 'reject' || deal.action === 'skip'
            ? 'shadow-[inset_3px_0_0_rgba(251,146,60,0.85)]'
            : isProblemRow(deal) ? 'shadow-[inset_3px_0_0_rgba(252,211,77,0.7)]' : ''
          const issues = issuesOf(deal)
          const open = opened.has(key)
          const listed = open ? issues : issues.slice(0, ISSUES_SHOWN)
          return (
            <tr
              key={key}
              data-testid="deal-book-row"
              data-deal={deal.deal}
              data-problem={isProblemRow(deal) ? 'true' : undefined}
              className={clsx('align-top', untouched && 'text-stone-500')}
            >
              <th
                scope="row"
                className={clsx(
                  'sticky left-0 z-10 border-b border-r border-b-white/[0.04] border-r-amber-300/10 bg-[#0c0c0c] px-2 py-1.5 text-left font-normal',
                  flag,
                )}
              >
                <span className={clsx('block w-[15rem] truncate font-medium', untouched ? 'text-stone-400' : 'text-stone-100')} title={deal.deal}>
                  {deal.cells?.property ?? deal.deal}
                </span>
                <span className="block w-[15rem] truncate text-[9px] text-stone-600" title={deal.rows.join(', ') || undefined}>
                  <span className={result.tone}>{result.label}</span>
                  {deal.rows.length ? ` · ${deal.rows.join(', ')}` : ''}
                </span>
              </th>
              <td
                className="border-b border-white/[0.04] px-2 py-1.5 text-left leading-snug"
                data-testid="deal-book-issues"
                title={!open && issues.length ? issues.map((issue) => issue.text).join('\n') : undefined}
              >
                <div className="w-[22rem] whitespace-normal">
                  {/* One line each until opened, so a row stays one scan high; all of it on hover. */}
                  {listed.map((issue) => (
                    <span key={issue.text} className={clsx('block', open ? '' : 'truncate', issue.tone)}>{issue.text}</span>
                  ))}
                  {issues.length > ISSUES_SHOWN || issues.some((issue) => issue.text.length > ONE_LINE) ? (
                    <button
                      type="button"
                      aria-expanded={open}
                      className="text-[9px] text-amber-300/80 underline-offset-2 hover:underline"
                      onClick={() => toggle(key)}
                    >
                      {open ? 'Show less' : issues.length > ISSUES_SHOWN ? `+${issues.length - ISSUES_SHOWN} more` : 'Show all'}
                    </button>
                  ) : null}
                  {!issues.length ? <span className="text-stone-600">—</span> : null}
                </div>
              </td>
              {fields.map((column) => {
                const gap = untouched ? undefined : gapIn(deal, column)
                const warned = untouched ? [] : warningsIn(deal, column)
                const assumed = untouched ? undefined : deal.defaults?.[column.id]
                const text = deal.cells?.[column.id] ?? '—'
                const why = gap
                  ? `Not in the file. ${gap.label}: ${gap.why}. The deal is created with it blank and opens in the Data Intelligence Review.`
                  : warned.length ? warned.join('\n') : assumed ?? (text.length > 28 ? text : undefined)
                return (
                  <td
                    key={column.id}
                    data-column={column.id}
                    data-gap={gap ? 'true' : undefined}
                    data-warning={!gap && warned.length ? 'true' : undefined}
                    data-default={assumed ? 'true' : undefined}
                    title={why}
                    className={clsx(
                      'whitespace-nowrap border-b border-white/[0.04] px-2 py-1.5',
                      gap
                        ? 'bg-amber-400/[0.08] text-amber-200 shadow-[inset_0_0_0_1px_rgba(252,211,77,0.45)]'
                        : warned.length
                          ? 'bg-orange-400/[0.06] text-orange-200 shadow-[inset_0_0_0_1px_rgba(251,146,60,0.4)]'
                          : text === '—' ? 'text-stone-600' : untouched ? 'text-stone-500' : 'text-stone-300',
                    )}
                  >
                    {/* A table cell ignores max-width under auto layout; the box inside it does not. */}
                    <div className="mx-auto flex max-w-[16rem] items-center justify-center gap-1">
                      <span className="min-w-0 truncate">{text}</span>
                      {gap ? <span className="shrink-0 rounded border border-amber-300/40 px-1 text-[8px] uppercase tracking-wide text-amber-200">review</span> : null}
                      {!gap && warned.length ? (
                        <span className="shrink-0 rounded border border-orange-300/40 px-1 text-[8px] uppercase tracking-wide text-orange-200">check</span>
                      ) : null}
                      {assumed ? (
                        <span className="shrink-0 rounded border border-dashed border-stone-500/50 px-1 text-[8px] text-stone-400">default</span>
                      ) : null}
                    </div>
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
